import { useEffect, useState } from "react"
import { motion } from "motion/react"
import { Link } from "react-router-dom"
import { useSession } from "@/hooks/use-session"

const DAY_MS = 24 * 60 * 60 * 1000

export function TrialBanner() {
  const session = useSession()
  const trialEndsAt =
    session.status === "authenticated" ? session.user.trial_ends_at ?? null : null
  const billingStatus =
    session.status === "authenticated" ? session.user.billing_status ?? null : null

  const target = trialEndsAt ? new Date(trialEndsAt).getTime() : NaN

  const [now, setNow] = useState(() => Date.now())
  const needsTimer =
    billingStatus === "trialing" && !Number.isNaN(target) && target - now <= DAY_MS
  useEffect(() => {
    if (!needsTimer) return
    const id = setInterval(() => setNow(Date.now()), 1000)
    return () => clearInterval(id)
  }, [needsTimer])

  if (session.status !== "authenticated") return null
  if (billingStatus !== "trialing" || Number.isNaN(target)) return null

  const remaining = target - now
  const expired = remaining <= 0
  const underDay = !expired && remaining <= DAY_MS
  const daysLeft = Math.max(0, Math.ceil(remaining / DAY_MS))

  return (
    <motion.div
      initial={{ opacity: 0, y: -4 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.25 }}
      className="border-b border-rose-500/20 bg-rose-500/6"
    >
      <div className="flex items-center justify-center gap-3 px-4 py-2 text-sm">
        <span className="relative inline-flex h-1.5 w-1.5">
          <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-rose-400 opacity-60" />
          <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-rose-400" />
        </span>
        {expired ? (
          <span className="text-foreground/80">
            Your trial has ended.
            <Link
              to="/upgrade"
              className="ml-2 font-medium text-rose-400 underline-offset-4 hover:underline"
            >
              Upgrade to continue
            </Link>
          </span>
        ) : underDay ? (
          <span className="text-foreground/80">
            <strong className="text-foreground tabular-nums">{formatCountdown(remaining)}</strong>{" "}
            left in your trial.
            <Link
              to="/upgrade"
              className="ml-2 font-medium text-rose-400 underline-offset-4 hover:underline"
            >
              Upgrade
            </Link>
          </span>
        ) : (
          <span className="text-foreground/80">
            <strong className="text-foreground">
              {daysLeft} day{daysLeft === 1 ? "" : "s"} left
            </strong>{" "}
            in your trial.
            <Link
              to="/upgrade"
              className="ml-2 font-medium text-rose-400 underline-offset-4 hover:underline"
            >
              Upgrade
            </Link>
          </span>
        )}
      </div>
    </motion.div>
  )
}

function formatCountdown(ms: number): string {
  const total = Math.floor(ms / 1000)
  const h = Math.floor(total / 3600)
  const m = Math.floor((total % 3600) / 60)
  const s = total % 60
  const p = (n: number) => String(n).padStart(2, "0")
  return `${p(h)}:${p(m)}:${p(s)}`
}
